import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Box, Button, Chip, CircularProgress, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, Table, TableBody, TableCell, TableHead, TablePagination, TableRow, TextField, Typography } from '@mui/material';
import { useRef, useState } from 'react';
import { useNotify, useRecordContext } from 'react-admin';
import { getAuthProviderFetchJson } from '../../lib/Auth';
import { superAdminApiServer } from '../../lib/config';

type CsvDataType = 'employees' | 'departments' | 'positions' | 'punches' | 'requests' | 'single-files';

interface CsvFileMetadata {
  integrationUuid: string;
  fileName: string;
  fileModifyTime: string;
  isValid: boolean;
  validationMessage: string | null;
  syncedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

const DATA_TYPES: { value: CsvDataType; label: string }[] = [
  { value: 'employees', label: 'Employees' },
  { value: 'departments', label: 'Departments' },
  { value: 'positions', label: 'Positions' },
  { value: 'punches', label: 'Punches' },
  { value: 'requests', label: 'Requests' },
  { value: 'single-files', label: 'Single Files' }
];

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

const FileHistoryTab = () => {
  const record = useRecordContext();
  const notify = useNotify();

  const [dataType, setDataType] = useState<CsvDataType>('employees');
  const [processDate, setProcessDate] = useState('');
  const [files, setFiles] = useState<CsvFileMetadata[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const integrationUuid = record?.uuid;

  const fetchFiles = (targetPage = 0, targetRowsPerPage = rowsPerPage) => {
    if (!integrationUuid) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setFiles([]);
    setLoading(true);

    const offset = targetPage * targetRowsPerPage;
    const params = new URLSearchParams({ dataType, limit: String(targetRowsPerPage), offset: String(offset) });
    if (processDate) params.set('processDate', processDate);

    const url = `${superAdminApiServer}/integrations/${integrationUuid}/csv-files?${params}`;
    getAuthProviderFetchJson(url, { signal: controller.signal })
      .then(({ json }) => {
        setFiles(json?.items ?? []);
        setTotal(json?.total ?? 0);
      })
      .catch((err) => {
        if (err?.name === 'AbortError') return;
        setTotal(0);
        notify('Failed to load file history', { type: 'error' });
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
  };

  const handleDataTypeChange = (e: SelectChangeEvent) => {
    abortRef.current?.abort();
    setDataType(e.target.value as CsvDataType);
    setFiles([]);
    setTotal(0);
    setPage(0);
    setLoading(false);
  };

  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
    fetchFiles(newPage, rowsPerPage);
  };

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newRows = parseInt(e.target.value, 10);
    setRowsPerPage(newRows);
    setPage(0);
    fetchFiles(0, newRows);
  };

  const handleDownload = async (file: CsvFileMetadata) => {
    const key = `${file.fileName}-${file.fileModifyTime}`;
    setDownloading(key);

    const params = new URLSearchParams({
      dataType,
      fileName: file.fileName,
      fileModifyTime: file.fileModifyTime
    });
    const url = `${superAdminApiServer}/integrations/${integrationUuid}/csv-files/download?${params}`;

    try {
      const { body, headers } = await getAuthProviderFetchJson(url);
      const contentDisposition = headers.get('content-disposition') ?? '';
      const match = contentDisposition.match(/filename="?([^";]+)"?/);
      const downloadName = match?.[1] ?? file.fileName;

      const blob = new Blob([body], { type: 'text/csv' });
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = downloadName;
      anchor.click();
      URL.revokeObjectURL(objectUrl);
    } catch {
      notify('Failed to download file', { type: 'error' });
    } finally {
      setDownloading(null);
    }
  };

  if (!integrationUuid) return null;

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Data Type</InputLabel>
          <Select value={dataType} label="Data Type" onChange={handleDataTypeChange}>
            {DATA_TYPES.map(({ value, label }) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField label="Process Date" type="date" size="small" value={processDate} onChange={(e) => setProcessDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: 180 }} />
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
          onClick={() => {
            setPage(0);
            fetchFiles(0);
          }}
          disabled={loading}
        >
          Fetch
        </Button>
        {loading && <CircularProgress size={20} sx={{ alignSelf: 'center' }} />}
      </Box>

      {!loading && files.length === 0 && (
        <Typography color="text.secondary" fontStyle="italic">
          No files found. Adjust the filters and click Fetch.
        </Typography>
      )}

      {files.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>File Name</TableCell>
              <TableCell>Modified At</TableCell>
              <TableCell>Valid</TableCell>
              <TableCell>Synced At</TableCell>
              <TableCell align="right">Download</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {files.map((file) => {
              const key = `${file.fileName}-${file.fileModifyTime}`;
              return (
                <TableRow key={key}>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{file.fileName}</TableCell>
                  <TableCell>{new Date(file.fileModifyTime).toLocaleString()}</TableCell>
                  <TableCell>
                    <Chip label={file.isValid ? 'Valid' : 'Invalid'} color={file.isValid ? 'success' : 'error'} size="small" />
                  </TableCell>
                  <TableCell>
                    {file.syncedAt ? (
                      new Date(file.syncedAt).toLocaleString()
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        Pending
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" startIcon={downloading === key ? <CircularProgress size={14} /> : <DownloadIcon />} disabled={downloading === key} onClick={() => handleDownload(file)}>
                      Download
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {total > 0 && <TablePagination component="div" count={total} page={page} rowsPerPage={rowsPerPage} rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS} onPageChange={handlePageChange} onRowsPerPageChange={handleRowsPerPageChange} />}
    </Box>
  );
};

export default FileHistoryTab;
